import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faMagic, faCopy } from '@fortawesome/free-solid-svg-icons';
import { Question, AnswersState } from '../types';
import { getStartTypeOptions } from '../config/startTypeOptions';
import styles from '../PromptGenerator.module.css';
import * as solidIcons from '@fortawesome/free-solid-svg-icons'; // Import all solid icons

// Helper function to dynamically get the icon object
const getIcon = (iconName: string) => {
  // Map string name back to the actual FontAwesome icon object
  const iconKey = Object.keys(solidIcons).find(key => key === iconName);
  return iconKey ? solidIcons[iconKey] : faMagic; // Fallback to faMagic
};

interface QuestionRendererProps {
  currentQuestion: Question | undefined;
  answers: AnswersState;
  isAdvancedMode: boolean;
  handleAnswer: (answer: string | string[]) => void;
  handleMultipleSelect: (questionId: string, optionId: string) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  setModalMediaType: (type: 'image' | 'video') => void;
  sceneDescription: string;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  currentQuestion,
  answers,
  isAdvancedMode,
  handleAnswer,
  handleMultipleSelect,
  setIsModalOpen,
  setModalMediaType,
  sceneDescription
}) => {
  const { t } = useTranslation('promptGenerator');
  
  if (!currentQuestion) return null;

  const renderOptions = () => {
    let options = currentQuestion.options;

    // Logic for dynamic startType options (Advanced Mode Only)
    if (currentQuestion.id === 'startType' && answers.outputType && isAdvancedMode) {
      options = getStartTypeOptions(answers.outputType as string);
    }
    
    // Fallback/Default renderer for options grid
    return (
      <div className={styles.optionsGrid}>
        {options.map((option) => {
          // Use labelKey if available, otherwise fallback to label
          const optionLabel = option.labelKey ? t(option.labelKey) : option.label;
          
          return (
            <button
              key={option.id}
              className={`${styles.optionCard} ${
                Array.isArray(answers[currentQuestion.id]) && (answers[currentQuestion.id] as string[]).includes(option.id)
                  ? styles.selected // Multi-select active state check
                  : answers[currentQuestion.id] === option.value
                  ? styles.selected // Single-select active state check (using value)
                  : ''
              }`}
              onClick={() => {
                if (currentQuestion.type === 'multiple') {
                  handleMultipleSelect(currentQuestion.id, option.id);
                } else {
                  handleAnswer(option.value || option.id);
                }
              }}
            >
              {option.icon && (
                <FontAwesomeIcon icon={getIcon(option.icon as string)} className={styles.optionIcon} />
              )}
              <span className={styles.optionLabel}>{optionLabel}</span>
            </button>
          );
        })}
      </div>
    );
  };
  
  const renderUploadPrompt = () => {
    return (
      <div className={styles.uploadPromptSection}>
        <p className={styles.uploadDescription}>
          {t('promptGenerator.uploadPrompt.description')}
        </p>
        
        {sceneDescription ? (
          <div className={styles.referenceAddedIndicator}>
            <div className={styles.referenceSuccess}>
              <FontAwesomeIcon icon={faCopy} />
              <span>{t('promptGenerator.uploadPrompt.referenceAdded')}</span>
            </div>
            <div className={styles.referencePreview}>
              {sceneDescription.substring(0, 150)}...
            </div>
            <button
              className={styles.addMoreButton}
              onClick={() => {
                setModalMediaType(answers.startType === 'continue-image' ? 'image' : 'video');
                setIsModalOpen(true);
              }}
            >
              {t('promptGenerator.uploadPrompt.addMore')}
            </button>
          </div>
        ) : (
          <div className={styles.uploadButtonContainer}>
            <button
              className={styles.uploadButton}
              onClick={() => {
                setModalMediaType('image');
                setIsModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faImage} />
              {t('promptGenerator.uploadPrompt.uploadImage')}
            </button>
            <button
              className={styles.uploadButton}
              onClick={() => {
                setModalMediaType('video');
                setIsModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faVideo} />
              {t('promptGenerator.uploadPrompt.uploadVideo')}
            </button>
          </div>
        )}
        
        <p className={styles.uploadNote}>
          {t('promptGenerator.uploadPrompt.optionalNote')}
        </p>
      </div>
    );
  };
  
  const renderTextQuestion = () => {
    return (
      <div className={styles.textInputSection}>
        <input
          type="text"
          className={styles.textInput}
          placeholder={currentQuestion.placeholder || t('promptGenerator.ideaInput.placeholder')}
          value={answers[currentQuestion.id] as string || ''}
          onChange={(e) => handleAnswer(e.target.value)}
        />
      </div>
    );
  };


  return (
    <div className={styles.questionContainer}>
      <h2 className={styles.questionTitle}>
        {currentQuestion.titleKey ? t(currentQuestion.titleKey) : currentQuestion.title}
      </h2>
      
      {currentQuestion.type === 'upload-prompt' ? renderUploadPrompt() :
       currentQuestion.type === 'text' ? renderTextQuestion() :
       renderOptions()
      }
    </div>
  );
};

export default QuestionRenderer;